import * as React from 'react'
import { ButtonBaseProps, ButtonBase } from '../ButtonBase'
import { StepContextType, useStepContext } from '../Step/StepContext'
import { StepperContextType, useStepperContext } from '../Stepper'
import { StepLabel } from '../StepLabel'
import { SxProps, sx as sxFunc } from '../sx'
import { isFactorElement } from '../utils'
import style from './index.module.scss'
import clsx from 'clsx'

export interface StepButtonClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `orientation="horizontal"`. */
  horizontal: string
  /** Styles applied to the root element if `orientation="vertical"`. */
  vertical: string
  /** Styles applied to the `ButtonBase` touch-ripple. */
  touchRipple: string
}

export type StepButtonIcon = React.ReactNode

export interface StepButtonProps extends ButtonBaseProps {
  /**
   * Can be a `StepLabel` or a node to place inside `StepLabel` as children.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<StepButtonClasses>
  /**
   * The icon displayed by the step label.
   */
  icon?: React.ReactNode
  /**
   * The optional node to display.
   */
  optional?: React.ReactNode
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

export const StepButton = React.forwardRef<HTMLButtonElement, StepButtonProps>(
  (props, ref) => {
    const { children, className, classes, icon, optional, sx, ...other } = props
    const { disabled, active } = useStepContext() as StepContextType
    const { orientation } = useStepperContext() as StepperContextType
    const childProps = {
      icon,
      optional,
    }

    const child = isFactorElement(children, ['StepLabel']) ? (
      // @ts-ignore
      React.cloneElement(children, childProps)
    ) : (
      <StepLabel {...childProps}>{children}</StepLabel>
    )

    return (
      <ButtonBase
        focusRipple
        disabled={disabled}
        TouchRippleProps={{
          className: clsx(classes?.touchRipple, style.touchRipple),
        }}
        className={clsx(
          style.root,
          classes?.root,
          style?.[`orientation-${orientation}`],
          classes?.[orientation],
          className,
          sxFunc(sx)
        )}
        ref={ref}
        aria-current={active ? 'step' : undefined}
        {...other}
      >
        {child}
      </ButtonBase>
    )
  }
)

StepButton.displayName = 'StepButton'
